import { __assign, __awaiter, __generator, __read } from "tslib";
import { FontIcon } from '@fluentui/react';
import { AvatarGroup, AvatarGroupItem, AvatarGroupPopover, Badge, Button, Caption1, mergeClasses, Overflow, OverflowItem, } from '@fluentui/react-components';
import { CheckmarkCircle12Filled, DismissCircle12Filled, Location12Regular, QuestionCircle12Filled, } from '@fluentui/react-icons';
import { getTimestampType } from '@office-start/date-utility';
import { fetchPersonPictureData } from '@office-start/graph-service';
import { getKey, KeyboardKey } from '@office-start/keystroke-handler-utility';
import { useTransformDateTimeString } from '@office-start/loc-service';
import { ResponseType, } from '@office-start/recommended-types';
import { format } from '@office-start/string-formatter-utility';
import { getBase64DataUri } from '@office-start/url-parser-utility';
import { getFileTypeIconProps } from '@uifabric/file-type-icons';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { MeetingInsightsOverflowMenu } from './MeetingInsightsOverflowMenu';
import { useCardStyles } from './views/RecommendedCardControl.styles';
export var AcceptedIcon = function () {
    return (React.createElement(Badge, { style: {
            color: '#13A10E',
            backgroundColor: 'white',
        }, size: "extra-small", icon: React.createElement(CheckmarkCircle12Filled, null) }));
};
export var DeclinedIcon = function () {
    return (React.createElement(Badge, { style: { color: '#D13438', backgroundColor: 'white' }, size: "extra-small", icon: React.createElement(DismissCircle12Filled, null) }));
};
export var TentativeIcon = function () {
    return (React.createElement(Badge, { style: { color: '#616161', backgroundColor: 'white' }, size: "extra-small", icon: React.createElement(QuestionCircle12Filled, null) }));
};
export var RecommendedMeetingPreviewControl = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var item = _a.item, index = _a.index, isComposite = _a.isComposite, PeopleCard = _a.PeopleCard, dateResources = _a.dateResources, preferredCulture = _a.preferredCulture, showInsights = _a.showInsights, isDarkMode = _a.isDarkMode, onDocumentClick = _a.onDocumentClick, onMeetingInsightsDocClick = _a.onMeetingInsightsDocClick, recommendedSectionResources = _a.recommendedSectionResources;
    var cardClasses = useCardStyles();
    var itemId = "rec-meeting-card-preview-item-".concat(index);
    var _h = __read(useState(), 2), inlineAttendeePictureUrls = _h[0], setInlineAttendeePictureUrls = _h[1];
    var _j = __read(useState(), 2), inlineAttendees = _j[0], setInlineAttendees = _j[1];
    var _k = __read(useState(), 2), overflowAttendees = _k[0], setOverflowAttendees = _k[1];
    var handleGetPictureUrl = useCallback(function (upn) { return __awaiter(void 0, void 0, void 0, function () {
        var personPictureData;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, fetchPersonPictureData(upn)];
                case 1:
                    personPictureData = (_a.sent()).result;
                    return [2 /*return*/, personPictureData];
            }
        });
    }); }, []);
    var componentRef = useRef(null);
    var meetingObjects = useMemo(function () {
        return item.documents || [];
    }, [item.documents]);
    var transformToDateTimeString = useTransformDateTimeString(dateResources, preferredCulture);
    var startsIn = '';
    var upcomingTime = (((_b = item.activity) === null || _b === void 0 ? void 0 : _b.timestamp) &&
        transformToDateTimeString((_c = item.activity) === null || _c === void 0 ? void 0 : _c.timestamp, false)) ||
        '';
    if ((((_d = item.activity) === null || _d === void 0 ? void 0 : _d.timestamp) &&
        getTimestampType((_e = item.activity) === null || _e === void 0 ? void 0 : _e.timestamp) ===
            9 /* TimestampType.InMinutes */) ||
        (((_f = item.activity) === null || _f === void 0 ? void 0 : _f.timestamp) &&
            getTimestampType((_g = item.activity) === null || _g === void 0 ? void 0 : _g.timestamp) ===
                10 /* TimestampType.InHours */)) {
        startsIn = format((recommendedSectionResources === null || recommendedSectionResources === void 0 ? void 0 : recommendedSectionResources.startsIn) || '', upcomingTime);
    }
    else {
        startsIn = upcomingTime || '';
    }
    var iconSize = 28;
    var getIconFromResponseStatus = function (response) {
        switch (response) {
            case ResponseType.Accepted:
            case ResponseType.Organizer:
                return React.createElement(AcceptedIcon, null);
            case ResponseType.Declined:
                return React.createElement(DeclinedIcon, null);
            case ResponseType.TentativelyAccepted:
                return React.createElement(TentativeIcon, null);
            default:
                return undefined;
        }
    };
    useEffect(function () {
        if (item.attendees) {
            var spliceIndex = Math.min(item.attendees.length, 4);
            setInlineAttendees(item.attendees.slice(0, spliceIndex));
            setOverflowAttendees(item.attendees.slice(spliceIndex, item.attendees.length));
        }
    }, [item.attendees]);
    useEffect(function () {
        var isMounted = true;
        var getPictureUrls = function () { return __awaiter(void 0, void 0, void 0, function () {
            var pictureData, inlineAttendeeImageUrls;
            return __generator(this, function (_a) {
                inlineAttendeeImageUrls = Array();
                inlineAttendees === null || inlineAttendees === void 0 ? void 0 : inlineAttendees.forEach(function (attendee) { return __awaiter(void 0, void 0, void 0, function () {
                    return __generator(this, function (_a) {
                        switch (_a.label) {
                            case 0: return [4 /*yield*/, handleGetPictureUrl(attendee.emailAddress.address)];
                            case 1:
                                pictureData = _a.sent();
                                inlineAttendeeImageUrls.push(pictureData
                                    ? getBase64DataUri('image/png', pictureData)
                                    : '');
                                return [2 /*return*/];
                        }
                    });
                }); });
                isMounted
                    ? setInlineAttendeePictureUrls(inlineAttendeeImageUrls)
                    : null;
                return [2 /*return*/];
            });
        }); };
        getPictureUrls();
        return function () {
            isMounted = false;
        };
    }, [item.attendees, handleGetPictureUrl, inlineAttendees]);
    var onKeyPress = useCallback(function (e) {
        var key = getKey(e);
        if (key !== KeyboardKey.Enter && key !== KeyboardKey.Space) {
            return;
        }
        onDocumentClick ? onDocumentClick() : null;
    }, [onDocumentClick]);
    var AvatarGroupElementArr = useMemo(function () {
        return inlineAttendees === null || inlineAttendees === void 0 ? void 0 : inlineAttendees.map(function (attendee, index) {
            var _a, _b;
            var avatarElement = (React.createElement(AvatarGroupItem, { image: {
                    src: (_a = (inlineAttendeePictureUrls &&
                        inlineAttendeePictureUrls[index])) !== null && _a !== void 0 ? _a : '',
                }, badge: ((_b = attendee.status) === null || _b === void 0 ? void 0 : _b.response) &&
                    getIconFromResponseStatus(attendee.status.response) !==
                        undefined
                    ? {
                        icon: getIconFromResponseStatus(attendee.status.response),
                    }
                    : undefined, name: attendee.emailAddress.name, key: attendee.emailAddress.name, "aria-label": attendee.emailAddress.name }));
            if (PeopleCard) {
                return (React.createElement(PeopleCard, { upn: attendee.emailAddress.address }, avatarElement));
            }
            else {
                return avatarElement;
            }
        });
    }, [inlineAttendees, inlineAttendeePictureUrls, PeopleCard]);
    return (React.createElement("div", { ref: componentRef, id: itemId, className: mergeClasses(cardClasses.meetingAndTaskInfo, cardClasses.meetingInfo, isDarkMode
            ? cardClasses.meetingInfoDark
            : cardClasses.meetingInfoLight), onClick: onDocumentClick, onKeyDown: onKeyPress, tabIndex: 0, role: "link", "aria-label": recommendedSectionResources === null || recommendedSectionResources === void 0 ? void 0 : recommendedSectionResources.exitCardHelperText },
        React.createElement("div", { className: cardClasses.meetingContent },
            isComposite ? (React.createElement(React.Fragment, null,
                React.createElement("div", { className: mergeClasses(cardClasses.meetingLocationLine, cardClasses.rowGap) },
                    React.createElement(Caption1, { className: mergeClasses(isDarkMode
                            ? cardClasses.compositeStartsInDark
                            : cardClasses.compositeStartsInLight, cardClasses.text, cardClasses.truncateText) }, startsIn),
                    React.createElement(Caption1, { className: mergeClasses(cardClasses.text, cardClasses.truncateText) }, "\u00B7"),
                    React.createElement(Caption1, { block: true, className: mergeClasses(cardClasses.text, cardClasses.truncateText) },
                        item.meetingStart,
                        " - ",
                        item.meetingEnd)))) : (React.createElement(Caption1, { block: true, className: mergeClasses(cardClasses.text, cardClasses.truncateText) },
                item.meetingStart,
                " - ",
                item.meetingEnd)),
            item.location && item.location !== '' ? (React.createElement("div", { className: cardClasses.meetingLocationLine },
                React.createElement(Location12Regular, null),
                React.createElement(Caption1, { className: mergeClasses(cardClasses.text, cardClasses.truncateText) }, item.location))) : null,
            React.createElement("div", null,
                React.createElement(AvatarGroup, { size: iconSize, className: mergeClasses(cardClasses.avatarGroup, isDarkMode
                        ? cardClasses.avatarGroupDarkMode
                        : cardClasses.avatarGroupLightMode) },
                    AvatarGroupElementArr && AvatarGroupElementArr.length
                        ? AvatarGroupElementArr.map(function (_attendee, index) {
                            return AvatarGroupElementArr[index];
                        })
                        : null,
                    overflowAttendees && overflowAttendees.length > 0 ? (React.createElement(AvatarGroupPopover, { open: false, tooltip: {
                            relationship: 'description',
                            positioning: 'below',
                            content: overflowAttendees.length === 1
                                ? (recommendedSectionResources === null || recommendedSectionResources === void 0 ? void 0 : recommendedSectionResources.oneMoreAttendee) ||
                                    ''
                                : format((recommendedSectionResources === null || recommendedSectionResources === void 0 ? void 0 : recommendedSectionResources.moreAttendees) ||
                                    '', overflowAttendees.length + ''),
                        } }, overflowAttendees === null || overflowAttendees === void 0 ? void 0 : overflowAttendees.map(function (attendee) { return attendee.emailAddress.name; }))) : null))),
        showInsights && meetingObjects ? (React.createElement(Overflow, null,
            React.createElement("div", { className: cardClasses.meetingInsights }, meetingObjects === null || meetingObjects === void 0 ? void 0 :
                meetingObjects.map(function (meetingObject, index) { return (React.createElement(OverflowItem, { key: index, id: index },
                    React.createElement(Button, { className: cardClasses.meetingInsightsButton, onClick: function (event) {
                            event.stopPropagation();
                            onMeetingInsightsDocClick &&
                                onMeetingInsightsDocClick(meetingObject, index, index, 'Recommended', event);
                        } },
                        React.createElement(FontIcon, __assign({ className: cardClasses.meetingInsightsIcon }, getFileTypeIconProps({
                            size: 16,
                            extension: meetingObject.extension,
                            imageFileType: 'svg',
                        }))),
                        React.createElement(Caption1, { block: true, className: mergeClasses(cardClasses.text, cardClasses.truncateText) }, meetingObject.title)))); }),
                React.createElement(MeetingInsightsOverflowMenu, { meetingObjects: meetingObjects, onMeetingInsightsDocClick: onMeetingInsightsDocClick })))) : null));
};
//# sourceMappingURL=RecommendedMeetingPreviewControl.js.map